// Sentry init for the React frontend.
//
// Prompt J close-out (Feb 2026) — safe-no-op wiring. Runs BEFORE the
// React tree renders so unhandled render errors + `console.error`
// events get captured. When REACT_APP_SENTRY_DSN is empty (local dev,
// preview without observability wired), this file is a total no-op
// — no network requests, no console noise, no perf cost.
//
// Env vars:
//   REACT_APP_SENTRY_DSN                — required to activate
//   REACT_APP_SENTRY_ENVIRONMENT        — "preview" | "production" | ...
//   REACT_APP_SENTRY_RELEASE            — immutable per-build id
//   REACT_APP_SENTRY_TRACES_SAMPLE_RATE — 0..1
//   REACT_APP_SENTRY_CAPTURE_CONSOLE    — "true" to send warn+error console

import * as Sentry from "@sentry/react";

const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();

/**
 * Clamp a numeric env-string to the 0..1 sample-rate range so a typo
 * doesn't send the SDK into "reject-all" territory silently.
 */
const asRate = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
};

export const sentryEnabled = Boolean(dsn);

if (sentryEnabled) {
  const integrations = [];
  // Capture warn+error console lines as breadcrumbs → helpful for
  // triage when a user reports "the sensitivity slider went weird".
  if (process.env.REACT_APP_SENTRY_CAPTURE_CONSOLE === "true") {
    integrations.push(
      Sentry.captureConsoleIntegration({ levels: ["warn", "error"] }),
    );
  }
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations,
    tracesSampleRate: asRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE),
    sendDefaultPii: false,
    // Only propagate trace headers to our own backend to avoid leaking
    // the SDK's tracestate onto Paddle/PayPal/Stripe requests.
    tracePropagationTargets: [
      "localhost",
      process.env.REACT_APP_BACKEND_URL || "",
    ].filter(Boolean),
  });
}

export { Sentry };
